import React from "react";
import { Row } from "reactstrap";
import SectionCard from "Components/Common/SectionCard";
import DetailField from "Components/Common/DetailField";
import SkeletonLoader from "Components/Common/ViewSkeleton";
import { formatDate, formatTime24Hour } from "common/service/CommonService";

interface AdverseEventFollowupViewTabProps {
  isLoading: boolean;
  isFetching: boolean;
  followups: any[];
}

const AdverseEventFollowupViewTab: React.FC<AdverseEventFollowupViewTabProps> = ({
  isLoading,
  isFetching,
  followups,
}) => {
  const loading = isLoading || isFetching;

  return (
    <>
      {/* Skeleton Loader during fetch */}
      {loading ? (
        <SkeletonLoader sections={1} fieldsPerSection={8} showHeader={false} />
      ) : (
        <>
          {/* Empty state */}
          {followups.length === 0 && (
            <SectionCard
              icon="ri-file-list-3-line"
              title="Follow-up Logs"
              subtitle="All follow-up check-ins recorded for this adverse event."
            >
              <div
                className="d-flex flex-column align-items-center justify-content-center py-5 text-center"
                style={{ minHeight: "220px" }}
              >
                <div
                  className="rounded-circle d-flex align-items-center justify-content-center mb-3"
                  style={{ width: 64, height: 64, background: "rgba(94,88,227,0.08)" }}
                >
                  <i className="ri-file-list-3-line" style={{ fontSize: 28, color: "#5e58e3" }} />
                </div>
                <h6 className="fw-semibold text-dark mb-1">No follow-ups recorded yet</h6>
                <p className="text-muted mb-0" style={{ fontSize: 13 }}>
                  Follow-up check-in records for this adverse event will appear here once added.
                </p>
              </div>
            </SectionCard>
          )}

          {/* Follow-up cards */}
          {followups.map((fu: any, index: number) => (
            <SectionCard
              key={fu.followupid || index}
              icon="ri-file-list-3-line"
              title={`Follow-up ${String(index + 1).padStart(2, "0")}`}
            >
              <Row>
                {/* Timing */}
                <DetailField label="Follow-up Date" value={formatDate(fu.followupdate)} truncate={true} />
                <DetailField label="Start Time" value={formatTime24Hour(fu.starttime)} truncate={true} />
                <DetailField label="End Time" value={formatTime24Hour(fu.endtime)} truncate={true} />

                {/* Vital Signs */}
                <DetailField label="Blood Pressure" value={fu.bloodpressure ? `${fu.bloodpressure} mm of Hg` : null} truncate={true} />
                <DetailField label="Pulse Rate" value={fu.pulse ? `${fu.pulse} bpm` : null} truncate={true} />
                <DetailField label="Respiratory Rate" value={fu.respiratoryrate ? `${fu.respiratoryrate} breaths/min` : null} truncate={true} />
                <DetailField
                  label="Temperature"
                  value={fu.temperature ? `${fu.temperature} °${fu.temperatureunit || "C"}${fu.temperaturelocation ? ` (${fu.temperaturelocation})` : ""}` : null}
                  truncate={true}
                />

                {/* Notes */}
                <DetailField label="Description" value={fu.description} columns={12} expandable={true} />
              </Row>
            </SectionCard>
          ))}
        </>
      )}
    </>
  );
};

export default AdverseEventFollowupViewTab;
